import React from 'react';
import { Link } from 'react-router-dom';
import { Bed, Bath, Maximize, ArrowRight, Heart, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';
import SmartImage from './SmartImage';

const PropertyCard = ({ property, priority = false }) => {
  const { t } = useLanguage();
  const { isFavorite, toggleFavorite, isInCompare, toggleCompare, compareCount, compareMax } = useFavorites();
  const fav = isFavorite(property.id);
  const cmp = isInCompare(property.id);

  const handleFav = (e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(property.id); };
  const handleCmp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!cmp && compareCount >= compareMax) return; // cap reached
    toggleCompare(property.id);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <Link
      to={`/woning/${property.id}`}
      style={{ textDecoration: 'none', color: 'inherit' }}
      data-testid="property-card-link"
    >
      <div style={styles.card} data-testid="property-card">
        <div style={styles.imageContainer}>
          <SmartImage
            src={property.images[0]}
            alt={property.title}
            width={600}
            srcset
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            style={{ width: '100%', height: '100%' }}
            imgStyle={{ transition: 'transform 0.5s ease' }}
            testId="property-image"
            priority={priority}
          />
          {property.featured && (
            <span style={styles.badge} data-testid="featured-badge">
              {t('property.featured')}
            </span>
          )}
          {property.new_build && (
            <span style={styles.newBadge}>{t('property.newBuild')}</span>
          )}
          {property.images.length > 1 && (
            <span style={styles.imageCount}>{property.images.length} {t('property.photos')}</span>
          )}
          {/* Favorite + Compare quick-actions overlay */}
          <div style={styles.quickActions}>
            <button
              type="button"
              onClick={handleFav}
              style={{ ...styles.iconBtn, ...(fav ? styles.iconBtnActiveFav : {}) }}
              aria-label={fav ? 'Verwijder favoriet' : 'Markeer favoriet'}
              data-testid={`favorite-toggle-${property.id}`}
            >
              <Heart size={16} fill={fav ? '#E11D48' : 'none'} color={fav ? '#E11D48' : '#1A1918'} />
            </button>
            <button
              type="button"
              onClick={handleCmp}
              style={{ ...styles.iconBtn, ...(cmp ? styles.iconBtnActiveCmp : {}) }}
              aria-label={cmp ? 'Verwijder uit vergelijking' : 'Voeg toe aan vergelijking'}
              title={!cmp && compareCount >= compareMax ? `Max ${compareMax} woningen vergelijken` : ''}
              disabled={!cmp && compareCount >= compareMax}
              data-testid={`compare-toggle-${property.id}`}
            >
              <BarChart3 size={16} color={cmp ? '#C5A059' : '#1A1918'} />
            </button>
          </div>
        </div>

        <div style={styles.content}>
          <div style={styles.header}>
            <h3 style={styles.title} data-testid="property-title">{property.title}</h3>
            <p style={styles.location} data-testid="property-location">{property.location}</p>
          </div>

          <div style={styles.specs}>
            <div style={styles.spec}>
              <Bed size={18} color="#8C8B88" />
              <span data-testid="property-bedrooms">{property.bedrooms}</span>
            </div>
            <div style={styles.spec}>
              <Bath size={18} color="#8C8B88" />
              <span data-testid="property-bathrooms">{property.bathrooms}</span>
            </div>
            <div style={styles.spec}>
              <Maximize size={18} color="#8C8B88" />
              <span data-testid="property-surface">{property.surface}m²</span>
            </div>
          </div>

          <div style={styles.footer}>
            <p style={styles.price} data-testid="property-price">{formatPrice(property.price)}</p>
            <span style={styles.button} data-testid="view-property-button">
              {t('property.viewProperty')}
              <ArrowRight size={14} style={{ marginLeft: '0.5rem' }} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const styles = {
  card: {
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  imageContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: '300px',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.5s ease',
  },
  badge: {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    backgroundColor: '#C5A059',
    color: 'white',
    padding: '0.5rem 1rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  newBadge: {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    backgroundColor: '#1A1918',
    color: 'white',
    padding: '0.4rem 0.85rem',
    fontSize: '0.7rem',
    fontWeight: 500,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  imageCount: {
    position: 'absolute',
    bottom: '1rem',
    left: '1rem',
    backgroundColor: 'rgba(26, 25, 24, 0.8)',
    color: 'white',
    padding: '0.25rem 0.75rem',
    fontSize: '0.75rem',
    borderRadius: '2px',
  },
  quickActions: {
    position: 'absolute',
    bottom: '1rem',
    right: '1rem',
    display: 'flex',
    gap: '0.4rem',
    zIndex: 2,
  },
  iconBtn: {
    width: '36px', height: '36px',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(4px)',
    border: 'none', cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    transition: 'background-color 0.15s ease, transform 0.15s ease',
  },
  iconBtnActiveFav: { backgroundColor: 'white' },
  iconBtnActiveCmp: { backgroundColor: '#1A1918' },
  content: {
    padding: '1.5rem',
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.25rem',
    color: '#1A1918',
    marginBottom: '0.5rem',
  },
  location: {
    color: '#8C8B88',
    fontSize: '0.875rem',
  },
  specs: {
    display: 'flex',
    gap: '1.5rem',
    marginBottom: '1.5rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #EAE5DD',
  },
  spec: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: '#585755',
    fontSize: '0.875rem',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  price: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.5rem',
    color: '#1A1918',
    fontWeight: 500,
  },
  button: {
    backgroundColor: '#C5A059',
    color: 'white',
    padding: '0.75rem 1.5rem',
    fontSize: '0.75rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    border: 'none',
    cursor: 'pointer',
  },
};

export default PropertyCard;
